import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import ShareButton from "@/components/ShareButton";

// Auto-advancing slideshow, appears on Home when there are visible slides.
export default function Slideshow() {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/slides").then((r) => setSlides(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  if (!slides.length) return null;

  const go = (n) => {
    setIdx(((n % slides.length) + slides.length) % slides.length);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
  };

  const current = slides[idx];
  const shareTitle = current?.caption
    ? `${current.caption} — B. Ruff · Writes`
    : "B. Ruff · Writes — Photos from the field";
  const shareExcerpt = current?.caption || "A moment from Barbara's mission trips.";

  return (
    <section
      data-testid="home-slideshow"
      className="bg-paper-2/40 border-b border-rule"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 pb-6">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <p className="text-xs uppercase tracking-[0.22em] text-sage">
            Photos from the field
          </p>
          <ShareButton
            testId="slideshow-share-btn"
            title={shareTitle}
            excerpt={shareExcerpt}
            label="Share these photos"
            url={typeof window !== "undefined" ? `${window.location.origin}/api/share/slideshow` : undefined}
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.2em] uppercase text-ink-soft hover:text-terracotta transition-colors border border-rule hover:border-terracotta"
          />
        </div>

        <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden border border-rule bg-paper">
          {slides.map((s, i) => (
            <div
              key={s.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              aria-hidden={i !== idx}
            >
              <img
                src={resolveImage(s.image_url)}
                alt={s.caption || `Slide ${i + 1}`}
                className="w-full h-full object-cover"
              />
              {s.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/80 to-transparent p-4 sm:p-6">
                  <p className="text-paper font-serif-display text-base sm:text-xl leading-snug max-w-3xl">
                    {s.caption}
                  </p>
                </div>
              )}
            </div>
          ))}

          {slides.length > 1 && (
            <>
              <button
                onClick={() => go(idx - 1)}
                data-testid="slideshow-prev"
                aria-label="Previous slide"
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-paper/80 hover:bg-paper text-ink p-2 rounded-full shadow-sm"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => go(idx + 1)}
                data-testid="slideshow-next"
                aria-label="Next slide"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-paper/80 hover:bg-paper text-ink p-2 rounded-full shadow-sm"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => go(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === idx ? "bg-paper" : "bg-paper/50 hover:bg-paper/80"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
